import { GuestAreaPromoBanner } from "@/components/GuestAreaPromoBanner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  useEliteReservation,
  useEliteStructure,
} from "@/contexts/EliteTemporaryContextProvider";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import type { WebCheckInReservationData } from "@/types/Reservation";
import {
  BedDouble,
  CheckCircle2,
  ConciergeBell,
  Hotel,
  KeyRound,
  LogIn,
  LogOut,
  MapPin,
  MapPinned,
  Phone,
  UserCheck,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export default function CheckInCompleted() {
  const { t } = useTranslation("checkIn");
  const navigate = useNavigate();
  useDocumentTitle(t("completed.title", "Check-in Online Completato!"));
  const { reservation } = useEliteReservation();
  const { structure } = useEliteStructure();

  const reservationData = reservation as WebCheckInReservationData | null;
  const res = reservationData?.reservation;
  const rooms = res?.rooms || [];
  const checkedInGuests = reservationData?.alreadyCheckedInGuests || [];

  const arrivalDate = res?.arrival ? new Date(res.arrival) : null;
  const departureDate = res?.departure ? new Date(res.departure) : null;

  return (
    <div className="max-w-2xl mx-auto space-y-6 py-6 px-3 sm:px-0 animate-in fade-in-50 duration-300">
      {/* Celebratory Hero */}
      <div className="flex flex-col items-center text-center space-y-3">
        <div className="size-16 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shadow-inner">
          <CheckCircle2 className="size-10" />
        </div>
        <div className="space-y-1">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight">
            {t("completed.title")}
          </h1>
          <p className="text-sm text-muted-foreground max-w-md">
            {t("completed.description")}
          </p>
        </div>
      </div>

      {/* Reservation & Stay Summary Card */}
      <Card className="overflow-hidden border border-border/80 shadow-sm py-0 gap-0">
        <CardHeader className="bg-muted/30 border-b p-4 sm:p-5 flex flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            {structure?.logo ? (
              <img
                src={structure.logo}
                alt={structure.name}
                className="max-h-8 max-w-[120px] object-contain"
              />
            ) : (
              <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Hotel className="size-4" />
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground uppercase font-semibold">
                {t("completed.confirmedStay")}
              </p>
              <h3 className="text-sm font-bold text-foreground">
                {structure?.name || t("completed.yourStructure")}
              </h3>
            </div>
          </div>

          <Badge variant="outline" className="font-mono text-xs">
            #{res?.idReservationPms || res?.id}
          </Badge>
        </CardHeader>

        <CardContent className="p-4 sm:p-5 space-y-5">
          {/* Date & Orari */}
          <div className="grid grid-cols-2 gap-3 p-3.5 rounded-xl bg-muted/20 border text-xs">
            <div className="space-y-1">
              <span className="text-muted-foreground flex items-center gap-1 font-medium">
                <LogIn className="size-3.5 text-emerald-500" />
                {t("completed.arrival")}
              </span>
              <p className="font-bold text-sm text-foreground">
                {arrivalDate ? arrivalDate.toLocaleDateString() : "-"}
              </p>
            </div>

            <div className="space-y-1 border-l pl-3">
              <span className="text-muted-foreground flex items-center gap-1 font-medium">
                <LogOut className="size-3.5 text-amber-500" />
                {t("completed.departure")}
              </span>
              <p className="font-bold text-sm text-foreground">
                {departureDate ? departureDate.toLocaleDateString() : "-"}
              </p>
            </div>
          </div>

          {/* Elenco Ospiti Registrati per Camera */}
          <div className="space-y-3">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <UserCheck className="size-4 text-primary" />
              {t("completed.guestsLabel", { count: checkedInGuests.length })}
            </h4>

            {rooms.length > 0 ? (
              rooms.map((room, rIdx) => {
                const roomGuests =
                  room.checkedInGuests && room.checkedInGuests.length > 0
                    ? room.checkedInGuests
                    : checkedInGuests.filter(
                        (g) =>
                          !g.idRisPren ||
                          g.idRisPren === room.idRisPren ||
                          rooms.length === 1,
                      );

                return (
                  <div
                    key={room.id || rIdx}
                    className="p-3 rounded-xl border bg-background space-y-2"
                  >
                    <div className="flex items-center justify-between text-xs font-semibold text-foreground">
                      <span className="flex items-center gap-1.5">
                        <BedDouble className="size-3.5 text-primary" />
                        {room.description ||
                          `${t("completed.roomLabel")} ${room.roomNumber || rIdx + 1}`}
                      </span>
                      <Badge variant="secondary" className="text-[10px]">
                        {t("completed.registeredBadge", {
                          count: roomGuests.length,
                          total: room.guestsNumber || 1,
                        })}
                      </Badge>
                    </div>

                     <div className="space-y-1 pl-5">
                      {roomGuests.map((g, gIdx) => (
                        <div
                          key={g.id || gIdx}
                          className="flex items-center justify-between text-xs text-muted-foreground py-0.5"
                        >
                          <span className="font-medium text-foreground">
                            {g.name} {g.surname}
                          </span>
                          <span className="text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                            <CheckCircle2 className="size-3" />
                            {t("completed.completedBadge")}
                          </span>
                        </div>
                      ))}
                      {roomGuests.length < (room.guestsNumber || 1) && (
                        <div className="pt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate("/check-in")}
                            className="w-full text-xs cursor-pointer"
                          >
                            + {t("room.insertGuest", {
                              current: roomGuests.length + 1,
                              total: room.guestsNumber || 1,
                              defaultValue: `Inserisci Dati Ospite (${roomGuests.length + 1} di ${room.guestsNumber || 1})`,
                            })}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="space-y-1.5">
                {checkedInGuests.map((g, gIdx) => (
                  <div
                    key={g.id || gIdx}
                    className="flex items-center justify-between p-2.5 rounded-lg border bg-background text-xs"
                  >
                    <span className="font-medium text-foreground">
                      {g.name} {g.surname}
                    </span>
                    <Badge
                      variant="default"
                      className="bg-emerald-600 text-[11px]"
                    >
                      {t("completed.completedBadge")}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick info note */}
          <div className="p-3.5 rounded-xl border border-primary/20 bg-primary/5 flex items-start gap-3 text-xs">
            <KeyRound className="size-5 text-primary shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              <p className="font-semibold text-foreground">
                {t("completed.whatToDo")}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {t("completed.whatToDoDesc", {
                  code: res?.idReservationPms || res?.id || "",
                })}
              </p>
            </div>
          </div>

          {/* Struttura Info & Contatti */}
          {structure && (
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t text-xs text-muted-foreground">
              {structure.address && (
                <span className="flex items-center gap-1">
                  <MapPin className="size-3.5 text-primary" />
                  {structure.address}, {structure.city}
                </span>
              )}
              {structure.tel && (
                <a
                  href={`tel:${structure.tel}`}
                  className="flex items-center gap-1 text-primary hover:underline font-medium"
                >
                  <Phone className="size-3.5" />
                  {structure.tel}
                </a>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Guest Area & Fidelity Promo */}
      <GuestAreaPromoBanner />

      {/* Action Buttons to Discover More */}
      <div className="space-y-3 pt-2 text-center">
        <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
          {t("completed.duringYourStay")}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button
            size="lg"
            onClick={() => navigate("/services")}
            className="w-full gap-2 py-6 font-semibold shadow-sm"
          >
            <ConciergeBell className="size-4" />
            {t("completed.viewServices")}
          </Button>

          <Button
            size="lg"
            variant="outline"
            onClick={() => navigate("/point-of-interest")}
            className="w-full gap-2 py-6 font-semibold"
          >
            <MapPinned className="size-4" />
            {t("completed.discoverPOI")}
          </Button>
        </div>
      </div>
    </div>
  );
}
